import { useState } from "react";
import { Share2, Copy, Check, Link2 } from "lucide-react";
import type { DecisionOption } from "@/lib/decisionEngine";
import { toast } from "@/hooks/use-toast";

interface Props {
  query: string;
  winner: DecisionOption;
}

export default function ShareDecision({ query, winner }: Props) {
  const [copied, setCopied] = useState<"link" | "summary" | null>(null);

  const shareUrl = `${window.location.origin}/?q=${encodeURIComponent(query)}`;

  const isEstimated = winner.verification === "ai-estimated";

  const summary = [
    `Decyzon result for "${query}"`,
    ``,
    `Winner: ${winner.productName}`,
    `Price: ${winner.price} | ${winner.deliveryTime}`,
    `Label: ${winner.label}`,
    ``,
    `${winner.explanation}`,
    ...(isEstimated ? [``, `Note: prices are AI estimates for this query. Re-run for live validation.`] : []),
    ``,
    `Run your own: ${shareUrl}`,
  ].join("\n");

  const copyToClipboard = async (text: string, type: "link" | "summary") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
      toast({ title: type === "link" ? "Link copied" : "Summary copied" });
    } catch {
      toast({ title: "Could not copy", description: "Try again." });
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share) {
      copyToClipboard(shareUrl, "link");
      return;
    }
    try {
      await navigator.share({
        title: `Best ${query} — Decyzon`,
        text: `Winner: ${winner.productName} (${winner.price}). ${winner.explanation}${isEstimated ? " (AI-estimated prices)" : ""}`,
        url: shareUrl,
      });
    } catch (e: any) {
      if (e.name !== "AbortError") {
        copyToClipboard(shareUrl, "link");
      }
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <button
        onClick={handleNativeShare}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-body font-medium text-muted-foreground hover:text-foreground hover:bg-muted/30 border border-border/30 transition-colors"
      >
        <Share2 className="w-3 h-3" />
        Share
      </button>
      <button
        onClick={() => copyToClipboard(shareUrl, "link")}
        className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-body text-muted-foreground hover:text-foreground hover:bg-muted/30 border border-border/30 transition-colors"
        title="Copy link"
      >
        {copied === "link" ? <Check className="w-3 h-3 text-score-high" /> : <Link2 className="w-3 h-3" />}
      </button>
      <button
        onClick={() => copyToClipboard(summary, "summary")}
        className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-body text-muted-foreground hover:text-foreground hover:bg-muted/30 border border-border/30 transition-colors"
        title="Copy summary"
      >
        {copied === "summary" ? <Check className="w-3 h-3 text-score-high" /> : <Copy className="w-3 h-3" />}
      </button>
    </div>
  );
}
